import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { apiError, fmtHour, type MessagingSettings, type UpdateMessagingSettingsInput } from './messagingLib';

interface BranchTz {
  id: string;
  name: string;
  timezone?: string;
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

/**
 * Quiet hours and the weekly fatigue cap. `TenantMessagingSettings` is
 * tenant-wide — there is no `branchId` on this endpoint, and no UI here
 * pretends otherwise.
 *
 * "Branch-first then tenant" is real code
 * (`MessagingSettingsService.resolveTimezone`), but it never actually takes
 * the branch branch today: a `Campaign` carries no `branchId` (ADR-0026 —
 * campaigns are tenant-wide broadcasts), so every quiet-hours check
 * currently resolves against the TENANT's own zone. This screen cannot read
 * that zone directly either — `Tenant.timezone` is only exposed through
 * `GET /tenants/:id`, gated on `platform.manage_tenants`, a key this
 * screen's own `campaigns.view`/`.manage` does not carry. Rather than
 * silently assume UTC or the browser's zone, this lists every branch's own
 * timezone (from `GET /branches`, which this actor CAN read) as the best
 * available proxy and says so.
 */
export default function SettingsTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('campaigns.manage');

  const [settings, setSettings] = useState<MessagingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<UpdateMessagingSettingsInput>({});
  const [branches, setBranches] = useState<BranchTz[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<MessagingSettings>('/messaging/settings')
      .then(({ data }) => {
        setSettings(data);
        setForm({
          quietHoursEnabled: data.quietHoursEnabled,
          quietHoursStart: data.quietHoursStart,
          quietHoursEnd: data.quietHoursEnd,
          maxMarketingMessagesPerWeek: data.maxMarketingMessagesPerWeek,
        });
      })
      .catch((e) => setError(apiError(e, t('messaging.settings.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  useEffect(() => {
    api
      .get('/branches', { params: { limit: 200 } })
      .then(({ data }) => setBranches((data?.data ?? data ?? []) as BranchTz[]))
      .catch(() => setBranches([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch<MessagingSettings>('/messaging/settings', form);
      setSettings(data);
      toast.success(t('messaging.settings.saved'));
    } catch (e) {
      toast.error(apiError(e, t('messaging.settings.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!settings) return null;

  const zones = [...new Set(branches.map((b) => b.timezone).filter(Boolean))] as string[];

  return (
    <div className="max-w-xl space-y-4">
      <Card>
        <CardContent className="flex items-start gap-2 p-4 text-sm text-ink-muted">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>{t('messaging.settings.timezoneNote')}</p>
            {zones.length > 0 && (
              <p className="mt-1">
                {t('messaging.settings.branchZones', { zones: zones.join(', ') })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <fieldset disabled={!canManage} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('messaging.settings.quietHours')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.quietHoursEnabled ?? true}
                onChange={(e) => setForm((f) => ({ ...f, quietHoursEnabled: e.target.checked }))}
                className="h-4 w-4"
              />
              {t('messaging.settings.quietHoursEnabled')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('messaging.settings.quietHoursStart')}>
                <Select
                  value={form.quietHoursStart ?? 21}
                  onChange={(e) => setForm((f) => ({ ...f, quietHoursStart: Number(e.target.value) }))}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {fmtHour(h)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('messaging.settings.quietHoursEnd')}>
                <Select
                  value={form.quietHoursEnd ?? 8}
                  onChange={(e) => setForm((f) => ({ ...f, quietHoursEnd: Number(e.target.value) }))}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {fmtHour(h)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <p className="text-xs text-ink-muted">{t('messaging.settings.quietHoursHint')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('messaging.settings.fatigueCap')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Field label={t('messaging.settings.maxPerWeek')} hint={t('messaging.settings.maxPerWeekHint')}>
              <Input
                type="number"
                min={0}
                value={form.maxMarketingMessagesPerWeek ?? 3}
                onChange={(e) => setForm((f) => ({ ...f, maxMarketingMessagesPerWeek: Number(e.target.value) }))}
              />
            </Field>
          </CardContent>
        </Card>

        {canManage && (
          <div className="flex justify-end">
            <Button disabled={saving} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </fieldset>
    </div>
  );
}
